/**
 * Premium Selection summary strip — the top-of-page cards the pricing
 * review call (Dario/Nathan/Linda/Débora/Henry) asked for (OR-1359,
 * Andressa's design). Left → right:
 *   Adjusted Premium · Selected Premium (highlighted) · SIR ·
 *   Loss Ratio (Full + Capped 5-year) · Expiring data (premium & SIR).
 *
 * The data-heavier cards sit at the end. Each single-value card skeletons
 * while a recalc that affects its value is in flight (AUTO-update spec).
 * Selected Premium / SIR echo the underwriter's own inputs, so they never
 * skeleton. Expiring premium/SIR are not exposed by the backend yet — the
 * card renders em dashes (forward-compat) until that field lands.
 */
import type { ReactNode } from "react";
import { Skeleton } from "../../../../components/skeleton";
import type { PremiumSelectionDerived } from "../../../../domain/pricing";
import { InfoTip } from "../components/field-row";
import { displayMoneyInput, displayMoneyValue, displayPercent } from "../premium-selection-types";
import { FIELD_TOOLTIPS, type FieldTooltip } from "../tooltips";
import type { DerivedKey, PendingMap } from "../use-premium-selection";

/** Card frame + header (label + info tooltip). The body is the caller's —
 *  a single value, or a stacked value + sub-line, etc. */
function SummaryCardShell({
    label,
    tooltip,
    highlighted,
    children,
    "data-autotest": dataAutotest,
}: {
    label: string;
    tooltip: FieldTooltip;
    /** Selected Premium gets the light-brand emphasis treatment. */
    highlighted?: boolean;
    children: ReactNode;
    "data-autotest"?: string;
}) {
    return (
        <div
            data-autotest={dataAutotest}
            className={
                highlighted
                    ? "border-brand bg-brand-primary shadow-xs flex flex-col gap-3 rounded-xl border px-5 py-5"
                    : "border-secondary bg-primary shadow-xs flex flex-col gap-3 rounded-xl border px-5 py-5"
            }
        >
            <div className="flex items-start justify-between gap-2">
                <span className="text-secondary text-sm font-semibold">{label}</span>
                <InfoTip tooltip={tooltip} />
            </div>
            {children}
        </div>
    );
}

/** The primary display value — skeletons while its recalc is pending. */
function CardValue({ value, isPending }: { value: string; isPending: boolean }) {
    return isPending ? (
        <Skeleton className="h-8 w-32" />
    ) : (
        <span key={value} className="text-primary value-resolve text-display-xs truncate font-semibold">
            {value}
        </span>
    );
}

interface PremiumSummaryCardsProps {
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
    /** Read-only echoes of the underwriter's Fees & Premium inputs. */
    selectedPremium: string;
    sir: string;
    /** Prior-term figures off the submission (Henry, jul-23): Premium ←
     *  expiring_premium, SIR ← expiring_deductible. */
    expiringPremium: string;
    expiringSir: string;
}

export function PremiumSummaryCards({ derived, pending, selectedPremium, sir, expiringPremium, expiringSir }: PremiumSummaryCardsProps) {
    const isPending = (key: DerivedKey) => Boolean(pending[key]) || derived === null;

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <SummaryCardShell label="Adjusted Premium" tooltip={FIELD_TOOLTIPS.adjustedPremium} data-autotest="premium-summary-card-adjusted-premium">
                <CardValue value={displayMoneyValue(derived?.adjustedPremium ?? null)} isPending={isPending("adjustedPremium")} />
            </SummaryCardShell>

            <SummaryCardShell label="Selected Premium" tooltip={FIELD_TOOLTIPS.selectedPremium} highlighted data-autotest="premium-summary-card-selected-premium">
                <CardValue value={displayMoneyInput(selectedPremium)} isPending={false} />
            </SummaryCardShell>

            <SummaryCardShell label="SIR" tooltip={FIELD_TOOLTIPS.sir} data-autotest="premium-summary-card-sir">
                <CardValue value={displayMoneyInput(sir)} isPending={false} />
            </SummaryCardShell>

            <SummaryCardShell label="Selected Loss Ratio" tooltip={FIELD_TOOLTIPS.fullLossRatio} data-autotest="premium-summary-card-selected-loss-ratio">
                <div className="flex flex-col gap-1.5">
                    <CardValue value={displayPercent(derived?.fullLossRatio ?? null)} isPending={isPending("fullLossRatio")} />
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="text-tertiary text-sm">Capped 5-year</span>
                        <span className="text-tertiary text-sm font-medium">{displayPercent(derived?.cappedLossRatio5yr ?? null)}</span>
                    </div>
                </div>
            </SummaryCardShell>

            <SummaryCardShell label="Expiring data" tooltip={FIELD_TOOLTIPS.expiringData} data-autotest="premium-summary-card-expiring-data">
                <div className="flex flex-col gap-1.5">
                    <ExpiringRow label="Premium" value={displayMoneyInput(expiringPremium)} />
                    <ExpiringRow label="SIR" value={displayMoneyInput(expiringSir)} />
                </div>
            </SummaryCardShell>
        </div>
    );
}

/** One labelled row inside the Expiring-data card. */
function ExpiringRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between gap-2">
            <span className="text-tertiary text-sm">{label}</span>
            <span className="text-primary truncate text-sm">{value}</span>
        </div>
    );
}
